import { Dispatch, SetStateAction } from 'react';

import { useIntl } from 'react-intl';

import ActionButton from 'components/action-button';
import { useAuthContext } from 'state/auth';
import { ModalAuthScreen, ModalAuthTrigger } from 'state/auth/types';

interface IWalmartFooter {
  isLoading?: boolean;
  disabled?: boolean;
  isAlreadyLinked?: boolean;
  authzCode?: string | string[] | null;
  eligibility?: any;
  linkCalled?: boolean;
  handleShouldRedirect?: Dispatch<SetStateAction<boolean>>;
  handleOpenWelcomeBackDrawer?: Dispatch<SetStateAction<boolean>>;
  handleShouldLink?: Dispatch<SetStateAction<boolean>>;
  handleRedirectToWalmartPlus?: () => void;
  handleRedeemOffers?: () => void;
}

export const WalmartFooter = ({
  isLoading = false,
  disabled = false,
  isAlreadyLinked = false,
  authzCode,
  eligibility,
  linkCalled = false,
  handleShouldRedirect,
  handleOpenWelcomeBackDrawer,
  handleShouldLink,
  handleRedirectToWalmartPlus,
  handleRedeemOffers,
}: IWalmartFooter) => {
  const { formatMessage } = useIntl();
  const { isAuthenticated, setModalAuthState } = useAuthContext();

  if (disabled) {
    return null;
  }

  if (isLoading) {
    return (
      <ActionButton fullWidth isLoading={true}>
        {' '}
      </ActionButton>
    );
  }

  if (isAuthenticated && !authzCode) {
    return (
      <ActionButton
        fullWidth
        onPress={isAlreadyLinked ? handleRedeemOffers : handleRedirectToWalmartPlus}
      >
        {formatMessage({ id: isAlreadyLinked ? 'redeemOffers' : 'walmartLinkAccounts' })}
      </ActionButton>
    );
  }

  // If no code provided, can assume user is coming from BK;
  // If not authenticated, prompt user to log in
  // At this point we know user is not linked
  if (!authzCode) {
    return (
      <ActionButton
        fullWidth
        onPress={() => {
          if (isAuthenticated) {
            return handleRedirectToWalmartPlus?.();
          }
          return setModalAuthState({
            screen: ModalAuthScreen.SIGN_IN,
            trigger: ModalAuthTrigger.Global,
            onSignInSuccess: () => {
              handleShouldRedirect?.(true);
            },
          });
        }}
      >
        {!isAuthenticated && !eligibility?.isExistingUser
          ? formatMessage({ id: 'walmartSignUpSignIn' })
          : formatMessage({ id: 'walmartLinkAccounts' })}
      </ActionButton>
    );
  }

  // If user eligible
  // If user not authenticated and not recognized BK user, then show sign up drawer
  // Otherwise show welcome back
  return (
    <ActionButton
      fullWidth
      isLoading={linkCalled}
      onPress={() => {
        if (!isAuthenticated && !eligibility?.isExistingUser) {
          return setModalAuthState({
            screen: ModalAuthScreen.SIGN_IN,
            trigger: ModalAuthTrigger.Global,
            user: {
              name: eligibility?.firstName || '',
            },
            onSignInSuccess: () => {
              handleShouldLink?.(true);
            },
          });
        }
        return handleOpenWelcomeBackDrawer?.(true);
      }}
    >
      {!isAuthenticated && !eligibility?.isExistingUser
        ? formatMessage({ id: 'walmartSignUpSignIn' })
        : formatMessage({ id: 'walmartLinkAccounts' })}
    </ActionButton>
  );
};
